const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://pdsci.on-forge.com/api';

export interface MaintenanceStatus {
    page_key: string;
    is_active: boolean;
    updated_at?: string | null;
    estimated_downtime?: string | null;
    support_email?: string | null;
    support_link?: string | null;
}

export interface MaintenanceCatalogItem {
    key: string;
    label: string;
    route: string;
    order?: number;
    enabled?: boolean;
    visible?: boolean;
    updated_at?: string | null;
}

const toMaintenanceStatus = (pageKey: string, raw: any): MaintenanceStatus => {
    return {
        page_key: raw?.page_key || raw?.pageKey || pageKey,
        is_active: Boolean(raw?.is_active ?? raw?.active ?? false),
        updated_at: raw?.updated_at ?? raw?.updatedAt ?? null,
        estimated_downtime: raw?.estimated_downtime ?? raw?.estimatedDowntime ?? raw?.downtime_message ?? null,
        support_email: raw?.support_email ?? raw?.supportEmail ?? null,
        support_link: raw?.support_link ?? raw?.supportLink ?? null,
    };
};

const toMaintenanceCatalogItem = (raw: any): MaintenanceCatalogItem | null => {
    const key = String(raw?.key || raw?.page_key || '').trim();
    const label = String(raw?.label || '').trim();
    const route = String(raw?.route || '').trim();

    if (!key || !label || !route) {
        return null;
    }

    return {
        key,
        label,
        route,
        order: typeof raw?.order === 'number' ? raw.order : undefined,
        enabled: typeof raw?.enabled === 'boolean' ? raw.enabled : Boolean(raw?.enabled),
        visible: raw?.visible === undefined ? true : Boolean(raw.visible),
        updated_at: raw?.updated_at ?? raw?.updatedAt ?? null,
    };
};

export const getMaintenanceCatalog = async (token?: string): Promise<MaintenanceCatalogItem[]> => {
    try {
        const response = await fetch(`${API_BASE_URL}/maintenance/catalog`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });

        if (!response.ok) {
            return [];
        }

        const data = await response.json();
        const payload = data?.data;
        const rows = Array.isArray(payload) ? payload : [];

        return rows
            .map(toMaintenanceCatalogItem)
            .filter((item): item is MaintenanceCatalogItem => item !== null);
    } catch {
        return [];
    }
};

export const getMaintenanceStatus = async (pageKey: string, token?: string): Promise<MaintenanceStatus | null> => {
    try {
        const response = await fetch(`${API_BASE_URL}/maintenance/${pageKey}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        const payload = data?.data ?? data;
        return toMaintenanceStatus(pageKey, payload);
    } catch {
        return null;
    }
};

export const updateMaintenanceStatus = async (
    pageKey: string,
    isActive: boolean,
    token?: string
): Promise<MaintenanceStatus | null> => {
    try {
        const response = await fetch(`${API_BASE_URL}/maintenance/${pageKey}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ is_active: isActive }),
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        const payload = data?.data ?? data;
        return toMaintenanceStatus(pageKey, payload);
    } catch {
        return null;
    }
};
